import React, { useCallback, useMemo } from 'react';

import { getOnlyFreeDoctorSchedule } from '../../../../../../../utils/getOnlyFreeDoctorSchedule';

import EmptySchedule from './empty';
import VerticalLineSchedule from './verticalLine';
import TimeSlot from './timeSlot';

import { ReactComponent as ScheduleInfoSvg } from '../../../../../../../assets/img/schedule-info.svg';
import moment from 'moment';


export default function DesktopSchedule({
  datesRange,
  jobDoctorScheduleSlots = {},
  jobDoctorScheduleDays = {},
  loading,
  onOpenAppointment,
  isDirect,
  searchFilters,
  doctor,
  job,
  patients,
  setDocСalendarDate,
}) {
  // Get all schedule slots grouped by date
  const allDoctorSchedule = useMemo(() => {
    if (!jobDoctorScheduleSlots?.schedule || jobDoctorScheduleSlots?.schedule.length === 0) {
      return {};
    }
    
    return jobDoctorScheduleSlots.schedule.reduce((acc, timeSlot) => {
      const day = moment(timeSlot.datetime).format('YYYY-MM-DD');
      
      // Check if this exact slot already exists in the array for this day
      const daySlots = acc[day] || [];
      const slotExists = daySlots.some(existingSlot => 
        existingSlot.job_id === timeSlot.job_id && 
        existingSlot.timestamp === timeSlot.timestamp
      );
      
      // Only add the slot if it doesn't already exist
      if (!slotExists) {
        return { ...acc, [day]: daySlots.concat(timeSlot) };
      }
      
      return acc;
    }, {});
  }, [jobDoctorScheduleSlots?.schedule]);
  
  // Get only FREE slots grouped by date
  const freeDoctorSchedule = useMemo(() => {
    // First get filtered slots with only FREE status
    const filteredSlots = getOnlyFreeDoctorSchedule(jobDoctorScheduleSlots?.schedule || []);
    
    // Then deduplicate within each day
    return Object.entries(filteredSlots).reduce((result, [day, slots]) => {
      // Filter out duplicates using job_id and timestamp as unique identifiers
      const uniqueSlots = slots.filter((slot, index, self) =>
        index === self.findIndex(s => 
          s.job_id === slot.job_id && 
          s.timestamp === slot.timestamp
        )
      );
      
      return { ...result, [day]: uniqueSlots };
    }, {});
  }, [jobDoctorScheduleSlots?.schedule]);
  
  // Get slots for the selected date when visit_date filter is present (moved before conditionals)
  const slotsForSelectedDate = useMemo(() => {
    if (!searchFilters.visit_date || !jobDoctorScheduleSlots?.schedule) return [];
    
    const selectedDate = moment(searchFilters.visit_date).format('YYYY-MM-DD');
    const slots = searchFilters.onlyAvailable === true
      ? freeDoctorSchedule[selectedDate] || []
      : allDoctorSchedule[selectedDate] || [];
      
    return slots;
  }, [searchFilters.visit_date, jobDoctorScheduleSlots?.schedule, freeDoctorSchedule, allDoctorSchedule, searchFilters.onlyAvailable]);

  const todayDoctorSchedule = useCallback((date) => {
    if (!jobDoctorScheduleDays?.schedule || jobDoctorScheduleDays?.schedule.length === 0) {
      return {};
    }
    
    return jobDoctorScheduleDays.schedule.find((item) => moment(item.time_from).format('YYYY-MM-DD') === date)

  }, [jobDoctorScheduleDays?.schedule]);


  if (loading) return <EmptySchedule placeholder="Завантаження ...." showLoader={true} />;

  if (!loading && (!jobDoctorScheduleDays?.schedule || jobDoctorScheduleDays?.schedule.length === 0)) {
    return (
      <EmptySchedule
        placeholder={
          <div className="schedule-body-no-result">
            <div className="schedule-body-no-result-content">
              <i className="fas fa-calendar-times" />
              Найближчим часом прийому немає
            </div>
          </div>
        }
      />
    );
  }
  
  return (
    <div className={`schedule-wrapper ${searchFilters.visit_date ? 'has_visit_date' : ''}`}>
      <div className="schedule">
        <div className="schedule-body">
          <div className="schedule-list">
            {searchFilters.visit_date ? (
              // When visit_date filter exists, display all individual time slots
              <>
                {slotsForSelectedDate.map((timeSlot) => (
                  <TimeSlot
                    key={`${doctor.id}-${timeSlot.job_id}-${timeSlot.timestamp}`}
                    timeSlot={timeSlot}
                    handleClickTimeSlot={onOpenAppointment}
                    job={job}
                    patients={patients} // Pass patients to TimeSlot
                  />
                ))}
                {slotsForSelectedDate.length === 0 && (
                  <div className="schedule-time-list__no-slots">
                    <div className="schedule-body-no-result-content">
                      <i className="fas fa-calendar-times"></i>
                      {searchFilters.visit_date && (() => {
                        const selectedDate = moment(searchFilters.visit_date).format('YYYY-MM-DD');
                        const schedule = todayDoctorSchedule(selectedDate);
                        
                        if (schedule?.status === 'PAST') {
                          return 'Прийом скінчився';
                        } else if (schedule?.status === 'ABSENT' && schedule?.absence_reason) {
                          return schedule.absence_reason;
                        } else {
                          return 'На обрану дату немає вільних слотів';
                        }
                      })()}
                    </div>
                  </div>
                )}
              </>
            ) : (
              // Regular view with vertical line schedule by dates
              datesRange.map((date) => (
                <div className='schedule-day' key={`${doctor.id}-${job.id}-${date}`}>
                  <VerticalLineSchedule
                    key={`${doctor.id}-${job.id}-${date}`}
                    schedule={todayDoctorSchedule(date)}
                    searchFilters={searchFilters}
                    setDocСalendarDate={setDocСalendarDate}
                    date={date}
                  />
                </div>
              ))
            )}
          </div>
          {searchFilters.visit_date && slotsForSelectedDate.length > 0 && isDirect && (
            <div className="schedule-info">
              <div className="schedule-info__icon">
                <ScheduleInfoSvg />
              </div>
              Запис можливий за направленням ЕСОЗ
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
